import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { THEME } from '../constants/theme';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';
import { useAnalytics } from '../hooks/useAnalytics';

// Move CommentInput to a separate component with memo
const CommentInput = React.memo(({ 
  value, 
  onChange, 
  onSubmit, 
  placeholder, 
  isReply = false, 
  parentId = null,
  subscription,
  aiLoading,
  onGetSuggestions,
  showSuggestions,
  suggestions,
  onSelectSuggestion,
  activeCommentId
}) => (
  <div className="mb-4">
    {/* AI Suggestions - Moved above input */}
    {showSuggestions && suggestions.length > 0 && 
     ((isReply && parentId === activeCommentId) || (!isReply && !activeCommentId)) && (
      <div className="mb-4 space-y-3">
        <div className="flex items-center">
          <SparklesIcon className="h-5 w-5 text-primary" />
          <span className="ml-2 text-sm font-medium text-primary">
            AI Suggestions
          </span>
        </div>
        <div className="space-y-2">
          {suggestions.map((suggestion, index) => (
            <button
              key={`${parentId || 'new'}-suggestion-${index}`}
              onClick={() => onSelectSuggestion(suggestion, isReply, parentId)}
              className="w-full p-4 text-left transition-all duration-200 bg-white rounded-xl border hover:border-primary/50 hover:shadow-lg group"
              style={{
                borderColor: `${THEME.primary}20`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="flex items-start">
                <div className="flex-1">
                  <p className="text-gray-700 group-hover:text-gray-900">{suggestion}</p>
                </div>
                <div className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <SparklesIcon className="h-4 w-4 text-primary" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    )}

    <div className="relative">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none min-h-[80px]"
        rows={3}
      />
      {subscription?.subscription_type === 'Paid' && (
        <button
          type="button"
          onClick={() => onGetSuggestions(value, isReply, parentId)}
          disabled={aiLoading}
          className={`absolute right-3 top-3 p-1.5 rounded-lg transition-all duration-200 ${
            aiLoading ? 'bg-primary/10' : 'hover:bg-gray-100'
          }`}
          title={aiLoading ? 'Generating suggestions...' : 'Get AI suggestions'}
        >
          <SparklesIcon 
            className={`h-5 w-5 ${aiLoading ? 'text-primary animate-pulse' : 'text-primary'}`}
          />
        </button>
      )}
    </div>

    <div className="mt-2 flex justify-end">
      <button
        onClick={(e) => onSubmit(e, parentId)}
        disabled={!value.trim()}
        className={`px-4 py-2 rounded-lg transition-all duration-200 ${
          value.trim() 
            ? 'bg-primary text-white hover:bg-primary-dark' 
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        Send
      </button>
    </div>
  </div>
));

export default function QuoteComments({ 
  quoteId, 
  user, 
  publicQuoteId, 
  isPublicView = false, 
  quoteOwnerId = null,
  businessName = 'Business Owner'
}) {
  const { logEvent } = useAnalytics();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTexts, setReplyTexts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);

  useEffect(() => {
    fetchComments();
    getSubscription();
    subscribeToComments();
  }, [quoteId]);

  const getSubscription = async () => {
    try {
      if (!user) return;
      const { data, error } = await supabase
        .from('subscriptions')
        .select('subscription_type')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quote_comments')
        .select('*')
        .eq('quote_id', quoteId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Organize comments into threads
      const threads = [];
      const commentMap = new Map();

      // First pass: Create a map of all comments
      data?.forEach(comment => {
        commentMap.set(comment.comment_id, { 
          ...comment, 
          replies: []
        });
      });

      // Second pass: Organize into threads
      data?.forEach(comment => {
        if (comment.parent_comment_id) {
          const parent = commentMap.get(comment.parent_comment_id);
          if (parent) {
            parent.replies.push(commentMap.get(comment.comment_id));
          }
        } else {
          threads.push(commentMap.get(comment.comment_id));
        }
      });

      setComments(threads);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToComments = () => {
    const subscription = supabase
      .channel(`quote-comments-${quoteId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'quote_comments',
          filter: `quote_id=eq.${quoteId}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            // For new comments, just fetch the updated data
            fetchComments();
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  const getSuggestions = async (inputText = '', isReply = false, parentCommentId = null) => {
    try {
      setAiLoading(true);
      setError(null);
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveCommentId(parentCommentId);

      const { data, error } = await supabase.functions.invoke('get-ai-suggestions', {
        body: { 
          quote_id: quoteId,
          current_input: inputText.trim() || null,
          is_reply: isReply,
          parent_comment_id: parentCommentId
        }
      });

      if (error) {
        console.error('Function error:', error);
        if (error instanceof FunctionsHttpError) {
          const errorData = await error.context.json();
          throw new Error(errorData.error || 'Server error');
        } else if (error instanceof FunctionsRelayError) {
          throw new Error('Failed to reach the server');
        } else if (error instanceof FunctionsFetchError) {
          throw new Error('Network error');
        } else {
          throw error;
        }
      }

      if (!data?.suggestions || !Array.isArray(data.suggestions)) {
        throw new Error('Invalid response format');
      }

      setSuggestions(data.suggestions);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error getting suggestions:', error);
      setError(error.message || 'Failed to get suggestions');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setAiLoading(false);
    }
  };

  const handleReplyChange = (id, value) => {
    setReplyTexts(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSelectSuggestion = (suggestion, isReply, parentId) => {
    if (isReply) {
      handleReplyChange(parentId, suggestion);
    } else {
      setNewComment(suggestion);
    }
    setShowSuggestions(false);
    setActiveCommentId(null);
  };

  const handleSubmitComment = async (e, parentId = null) => {
    e.preventDefault();
    const commentText = parentId ? replyTexts[parentId] : newComment;
    
    if (!commentText?.trim()) return;

    try {
      logEvent('QuoteComments', 'Add Comment', `Quote ID: ${quoteId}`);
      const { error } = await supabase
        .from('quote_comments')
        .insert([{
          quote_id: quoteId,
          public_quote_id: publicQuoteId,
          user_id: user?.id || null,
          user_email: user?.email || null,
          comment: commentText.trim(),
          is_reply: !!parentId,
          is_client: user?.id === quoteOwnerId ? false : true,
          parent_comment_id: parentId
        }]);

      if (error) throw error;

      if (parentId) {
        setReplyTexts(prev => ({ ...prev, [parentId]: '' }));
      } else {
        setNewComment('');
      }
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveCommentId(null);
      logEvent('QuoteComments', 'Add Comment Success', `Quote ID: ${quoteId}`);
    } catch (error) {
      console.error('Error submitting comment:', error);
      setError('Failed to submit comment. Please try again.');
      logEvent('QuoteComments', 'Add Comment Error', error.message);
    }
  };

  const canReply = (commentUserId) => {
    if (isPublicView) {
      // In public view, only quote owner can reply
      return user?.id === quoteOwnerId;
    }
    // In private view, logged-in user can reply
    return !!user;
  };

  const getSenderName = (comment) => {
    if (comment.user_id === quoteOwnerId) {
      return businessName;
    }
    return 'Client';
  };

  const handleDeleteComment = async (commentId) => {
    try {
      logEvent('QuoteComments', 'Delete Comment', `Comment ID: ${commentId}`);
      const { error } = await supabase
        .from('quote_comments')
        .delete()
        .eq('comment_id', commentId);

      if (error) throw error;

      fetchComments();
      logEvent('QuoteComments', 'Delete Comment Success', `Comment ID: ${commentId}`);
    } catch (error) {
      console.error('Error deleting comment:', error);
      logEvent('QuoteComments', 'Delete Comment Error', error.message);
    }
  };

  const handleEditComment = async (commentId, newText) => {
    try {
      logEvent('QuoteComments', 'Edit Comment', `Comment ID: ${commentId}`);
      const { error } = await supabase
        .from('quote_comments')
        .update({ comment: newText })
        .eq('comment_id', commentId);

      if (error) throw error;

      fetchComments();
      logEvent('QuoteComments', 'Edit Comment Success', `Comment ID: ${commentId}`);
    } catch (error) {
      console.error('Error editing comment:', error);
      logEvent('QuoteComments', 'Edit Comment Error', error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* New comment input */}
      {(isPublicView || user) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <h3 className="font-medium text-gray-900 mb-3">Add a comment</h3>
          <CommentInput
            value={newComment}
            onChange={setNewComment}
            onSubmit={handleSubmitComment}
            placeholder="Write your comment..."
            subscription={subscription}
            aiLoading={aiLoading}
            onGetSuggestions={getSuggestions}
            showSuggestions={showSuggestions}
            suggestions={suggestions}
            onSelectSuggestion={handleSelectSuggestion}
            activeCommentId={activeCommentId}
          />
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-4">
        {comments.map((comment) => (
          <div 
            key={comment.comment_id}
            className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
          >
            {/* Main comment */}
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-medium">
                      {comment.user_email ? comment.user_email[0].toUpperCase() : ''}
                    </span>
                  </div>
                  <div className="ml-3">
                    <span className="font-medium text-gray-900">
                      {getSenderName(comment)}
                    </span>
                    <span className="text-sm text-gray-500 ml-2">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 ml-11">{comment.comment}</p>

              {/* Thread replies */}
              {comment.replies.length > 0 && (
                <div className="mt-4 ml-11 space-y-3">
                  {comment.replies.map((reply) => (
                    <div 
                      key={reply.comment_id}
                      className="pl-4 border-l-2 border-gray-100"
                    >
                      <div className="flex items-start">
                        <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary text-sm font-medium">
                            {reply.user_email[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3 flex-1">
                          <div className="flex items-center">
                            <span className="font-medium text-sm text-gray-900">
                              {getSenderName(reply)}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                              {new Date(reply.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{reply.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reply input */}
            {canReply(comment.user_id) && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                <CommentInput
                  value={replyTexts[comment.comment_id] || ''}
                  onChange={(value) => handleReplyChange(comment.comment_id, value)}
                  onSubmit={handleSubmitComment}
                  placeholder="Reply to this comment..."
                  isReply={true}
                  parentId={comment.comment_id}
                  subscription={subscription}
                  aiLoading={aiLoading}
                  onGetSuggestions={getSuggestions}
                  showSuggestions={showSuggestions}
                  suggestions={suggestions}
                  onSelectSuggestion={handleSelectSuggestion}
                  activeCommentId={activeCommentId}
                />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        )}

        {!loading && comments.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-gray-500">No comments yet</p>
            <p className="text-sm text-gray-400 mt-1">Be the first to start the conversation</p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 rounded-lg p-3 border border-red-100">
          {error}
        </div>
      )}
    </div>
  );
} 